var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { createHawkinsDataAttributeName, isHTMLElement, isNil, logToConsole, useIsomorphicLayoutEffect, } from '@hawkins/helpers';
import { Menu } from '../menu';
import { MenuItem } from '../menuItem';
import { useDynamicLayoutCalculation } from './useDynamicLayoutCalculation';
var displayName = 'useOverflowMenu';
export var USE_OVERFLOW_MENU_MEASURABLE_ELEM_ATTR = createHawkinsDataAttributeName('measurableElem', {
    namespace: displayName,
});
export var USE_OVERFLOW_MENU_MENU_TRIGGER_ATTR = createHawkinsDataAttributeName('menuTrigger', {
    namespace: displayName,
});
export var USE_OVERFLOW_MENU_OVERFLOWABLE_ELEM_ATTR = createHawkinsDataAttributeName('overflowableElem', {
    namespace: displayName,
});
var defaultGetMeasurableElems = function (children, _a) {
    var measurableElemAttr = _a.measurableElemAttr;
    return React.Children.toArray(children)
        .filter(React.isValidElement)
        .map(function (child) {
        var _a, _b;
        var assumedMenuTrigger = child.props.trigger;
        if (assumedMenuTrigger) {
            // If the child has a `trigger` prop, assume it's a `Menu`.
            // For a `Menu`, the trigger is the measurable element.
            return React.cloneElement(child, {
                trigger: React.cloneElement(assumedMenuTrigger, (_a = {}, _a[measurableElemAttr] = true, _a)),
            });
        }
        // A conservative default behavior is to set `measurableElemAttr` on every top-level element.
        // It is recommended that clients use a React context to set `overflowableElemAttr`
        // ONLY on components that make sense to render as a `MenuItem` inside a `Menu`.
        // Otherwise, arbitrary elements could be included in the `Menu` that shouldn't be.
        return React.cloneElement(child, (_b = {}, _b[measurableElemAttr] = true, _b));
    });
};
/**
 * A hook that assists in calculating and displaying an overflow menu.
 *
 * TODO(API): Write docs and tests and expose to Hawkins users.
 * Should probably add a `result.style` property with inline styles that should be set
 * on the overflowable container for the overflow calculation to work.
 *
 * @private
 */
export function useOverflowMenu(_a) {
    var children = _a.children, _b = _a.disabled, disabled = _b === void 0 ? false : _b, _c = _a.disableOverflowableElemsWarning, disableOverflowableElemsWarning = _c === void 0 ? false : _c, displayNameProp = _a.displayName, _d = _a.getMeasurableElems, getMeasurableElems = _d === void 0 ? defaultGetMeasurableElems : _d, getMenuItemProps = _a.getMenuItemProps, getMenuPropsProp = _a.getMenuProps, maxDisplayElems = _a.maxDisplayElems, _e = _a.measurableElemAttr, measurableElemAttr = _e === void 0 ? USE_OVERFLOW_MENU_MEASURABLE_ELEM_ATTR : _e, _f = _a.menuTriggerAttr, menuTriggerAttr = _f === void 0 ? USE_OVERFLOW_MENU_MENU_TRIGGER_ATTR : _f, _g = _a.overflowableElemAttr, overflowableElemAttr = _g === void 0 ? USE_OVERFLOW_MENU_OVERFLOWABLE_ELEM_ATTR : _g;
    // Params
    var getMenuProps = React.useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return (__assign({ position: 'bottom', showCaret: true }, getMenuPropsProp.apply(void 0, __spreadArray([], __read(args), false))));
    }, [getMenuPropsProp]);
    // Refs
    var containerRef = React.useRef(null);
    var overflowElemsRef = React.useRef([]);
    var _h = useDynamicLayoutCalculation({ ref: containerRef }), isLayoutCalculatable = _h.isLayoutCalculatable, width = _h.width;
    // State
    var _j = __read(React.useState(undefined), 2), displayElems = _j[0], setDisplayElems = _j[1];
    // Trigger an overflow calculation whenever the children, container width, or relevant props change.
    useIsomorphicLayoutEffect(function () {
        if (!isLayoutCalculatable || disabled)
            return;
        setDisplayElems(null);
    }, [children, disabled, isLayoutCalculatable, maxDisplayElems, width]);
    // Calculate the overflow elements.
    useIsomorphicLayoutEffect(function () {
        if (displayElems !== null)
            return;
        var measurableElems = getMeasurableElems(children, { measurableElemAttr: measurableElemAttr, overflowableElemAttr: overflowableElemAttr });
        var calculatedSliceEndIdx = measurableElems.length;
        var containerEl = containerRef.current;
        var menuTriggerEl = containerEl === null || containerEl === void 0 ? void 0 : containerEl.querySelector("[".concat(menuTriggerAttr, "]"));
        var measurableEls = Array.from((containerEl === null || containerEl === void 0 ? void 0 : containerEl.querySelectorAll("[".concat(measurableElemAttr, "]"))) || [])
            .filter(isHTMLElement)
            // Just in case an element has both `measurableElemAttr` and `menuTriggerAttr` at the same time, filter it out.
            .filter(function (el) { return !el.getAttribute(menuTriggerAttr); });
        var firstMeasurableEl = measurableEls[0];
        var lastMeasurableEl = measurableEls[measurableEls.length - 1];
        if (!containerEl) {
            logToConsole('The overflow menu functionality is being skipped. Is `containerRef` being set on the overflowable container element?', { displayName: displayNameProp });
        }
        else if (!(menuTriggerEl instanceof HTMLElement)) {
            logToConsole('The overflow menu functionality is being skipped. Is `contentNode` being rendered inside the overflowable container element?', { displayName: displayNameProp });
        }
        else if (measurableElems.length !== measurableEls.length) {
            logToConsole([
                'The overflow menu functionality is being skipped. Does every React element returned by `getMeasurableElems` have `measurableElemAttr` set on its underlying root HTML element?',
                "measurableElems.length=".concat(measurableElems.length, ", measurableEls.length=").concat(measurableEls.length),
            ].join(' '), { displayName: displayNameProp });
        }
        else if (firstMeasurableEl && lastMeasurableEl) {
            var overflowableEls = Array.from(containerEl.querySelectorAll("[".concat(overflowableElemAttr, "]")))
                .filter(isHTMLElement)
                // Just in case an element has both `overflowableElemAttr` and `menuTriggerAttr` at the same time, filter it out.
                .filter(function (el) { return !el.getAttribute(menuTriggerAttr); });
            if (measurableEls.length !== overflowableEls.length && !disableOverflowableElemsWarning) {
                logToConsole([
                    'If the content in this component overflows, not all elements will be included in the overflow menu. Did you set `overflowableElemAttr` on all relevant elements?',
                    'Set `props.disableOverflowableElemsWarning=true` to disable this warning ONLY if you are certain that you are handling overflow for all your elements correctly.',
                    "measurableEls.length=".concat(measurableEls.length, ", overflowableEls.length=").concat(overflowableEls.length),
                ].join(' '), { displayName: displayNameProp });
            }
            var clientWidth = containerEl.clientWidth;
            var firstElLeftPos = firstMeasurableEl.getBoundingClientRect().left;
            if (lastMeasurableEl.getBoundingClientRect().right - firstElLeftPos > clientWidth) {
                var menuTriggerWidth = menuTriggerEl.offsetWidth;
                calculatedSliceEndIdx--;
                for (var i = measurableEls.length - 1; i >= 1 && calculatedSliceEndIdx > 0; i--) {
                    var prevLastEl = measurableEls[i];
                    if (!prevLastEl ||
                        prevLastEl.getBoundingClientRect().left - firstElLeftPos + menuTriggerWidth <= clientWidth) {
                        break;
                    }
                    calculatedSliceEndIdx--;
                }
            }
        }
        var finalSliceEndIdx = isNil(maxDisplayElems)
            ? calculatedSliceEndIdx
            : Math.min(calculatedSliceEndIdx, maxDisplayElems);
        var overflowElems = measurableElems.slice(finalSliceEndIdx);
        var overflowEls = measurableEls.slice(finalSliceEndIdx);
        setDisplayElems(measurableElems.slice(0, finalSliceEndIdx));
        // `measurableElems` and `measurableEls` should be the same size (we check for this above),
        // so `overflowElems` and `overFlowEls` should also be the same size.
        // We assume that the caller has set these up to correctly relate to each other,
        // so we can check if a member of `overflowElems` should be rendered inside the overflow menu
        // based on its corresponding member in `overflowEls`.
        overflowElemsRef.current = overflowElems.filter(function (_, idx) { return overflowEls[idx].getAttribute(overflowableElemAttr); });
        // `displayElems` is the only trigger we want for this effect.
        // Other relevant variables (like `maxDisplayElems`) are accounted for by the effect that sets `displayElems` to `null`.
        // If we included those variables in this dependency array, bugs could occur due to the effects executing in the wrong order.
        //
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayElems]);
    var isDone = disabled || !!displayElems;
    var contentNode = null;
    if (isDone) {
        contentNode = disabled ? (children) : (_jsxs(_Fragment, { children: [displayElems, !!overflowElemsRef.current.length && (_jsx(Menu, __assign({}, getMenuProps({ isDone: isDone, menuTriggerAttr: menuTriggerAttr, overflowElemsRef: overflowElemsRef }), { children: overflowElemsRef.current.map(function (elem, idx) { return (_jsx(MenuItem, __assign({}, getMenuItemProps(elem, { idx: idx })))); }) })))] }));
    }
    else if (displayElems === null) {
        // Render dummy content so we can measure the resulting DOM elements and calculate the overflow.
        contentNode = (_jsxs(_Fragment, { children: [getMeasurableElems(children, { measurableElemAttr: measurableElemAttr, overflowableElemAttr: overflowableElemAttr }), _jsx(Menu, __assign({}, getMenuProps({ isDone: isDone, menuTriggerAttr: menuTriggerAttr, overflowElemsRef: overflowElemsRef })))] }));
    }
    return { containerRef: containerRef, contentNode: contentNode, displayElems: displayElems, isDone: isDone, overflowElemsRef: overflowElemsRef };
}
